import React, { useState, useRef, useEffect } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import frLocale from '@fullcalendar/core/locales/fr';
import { useCarCalendarPrices, useEditCarPrice, useToggleCarPrice, CarCalendarPrice } from '../api/carCalendarApi';
import { CarPriceEditModal } from './CarPriceEditModal';
import { EditPriceRequest } from '../api/carCalendarApi';
import Button from '@/components/ui/button/Button';
import { useModal } from '@/shared/hooks/useModal';
import { useTranslation } from 'react-i18next';
import { useToast } from '@/components/ui/toast/Toast';

interface Props {
  carId: string;
  defaultPrice: number;
}

export const CarPriceCalendar: React.FC<Props> = ({ carId, defaultPrice }) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const editPrice = useEditCarPrice();
  const togglePrice = useToggleCarPrice();
  const [selectedPrice, setSelectedPrice] = useState<CarCalendarPrice | null>(null);
  const previousDataRef = useRef<{ prices: CarCalendarPrice[] } | null>(null);
  const { isOpen: isPriceRangeEditOpen, openModal: openPriceRangeEditModal, closeModal: closePriceRangeEditModal } = useModal();

  const { data: calendarData, isLoading } = useCarCalendarPrices(
    carId,
    currentDate.getFullYear(),
    currentDate.getMonth() + 1
  );

  // Update previous data reference when new data arrives
  if (calendarData && !isLoading) {
    previousDataRef.current = calendarData;
  }

  // Use previous data while loading new data
  const displayData = isLoading ? previousDataRef.current : calendarData;

  const displayDataRef = useRef(displayData);
  displayDataRef.current = displayData;

  const handleDateClick = (info: { date: Date; jsEvent: MouseEvent }) => {
    if ((info.jsEvent.target as HTMLElement).closest('.price-eye-btn')) return;

    const dateStr = info.date.toLocaleDateString('en-CA');
    const existingPrice = displayData?.prices.find(
      price => price.active_at === dateStr
    );

    setSelectedDate(info.date);
    setSelectedPrice(existingPrice || null);
  };

  const handleDatesSet = (dateInfo: any) => {
    const date = dateInfo.view.currentStart;
    setCurrentDate(date);
  };

  const handleEditPrice = async (data: EditPriceRequest) => {
    try {
      await editPrice.mutateAsync(data);
      setSelectedDate(null);
      setSelectedPrice(null);
    } catch (error) {
      throw error;
    }
  };

  const handleTogglePrice = async (date: Date) => {
    const dateStr = date.toLocaleDateString('en-CA');
    const existingPrice = displayDataRef.current?.prices?.find(p => p.active_at === dateStr);
    const newIsActive = existingPrice ? !existingPrice.is_active : false;

    try {
      await togglePrice.mutateAsync({
        car: carId,
        from_dt: dateStr,
        till_dt: dateStr,
        is_active: newIsActive,
      });
    } catch (error) {
      toast.error(t('cars.price_edit.toggle_error') + `<br><br>${error}`);
    }
  };

  const handleTogglePriceRef = useRef(handleTogglePrice);
  handleTogglePriceRef.current = handleTogglePrice;

  const eyeOpenSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;
  const eyeClosedSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/><path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/><path d="M14.12 14.12a3 3 0 1 1-4.24-4.24"/></svg>`;

  useEffect(() => {
    if (!displayData) return;

    const dayCells = document.querySelectorAll('.fc-daygrid-day[data-date]');

    dayCells.forEach((cell) => {
      const dayEl = cell as HTMLElement;
      const dateStr = dayEl.getAttribute('data-date')!;

      const existingPrice = displayDataRef.current?.prices?.find(p => p.active_at === dateStr);
      if (!existingPrice) return;

      const isActive = existingPrice.is_active;

      let eyeBtn = dayEl.querySelector('.price-eye-btn') as HTMLElement;

      if (!eyeBtn) {
        dayEl.style.position = 'relative';

        eyeBtn = document.createElement('div');
        eyeBtn.className = 'price-eye-btn';

        const [y, m, d] = dateStr.split('-').map(Number);
        const date = new Date(y, m - 1, d);

        eyeBtn.addEventListener('click', (e) => {
          e.stopPropagation();
          e.preventDefault();
          handleTogglePriceRef.current(date);
        });

        dayEl.appendChild(eyeBtn);
      }

      eyeBtn.innerHTML = isActive ? eyeOpenSvg : eyeClosedSvg;
      Object.assign(eyeBtn.style, {
        position: 'absolute',
        bottom: '8px',
        right: '8px',
        zIndex: '20',
        cursor: 'pointer',
        width: '32px',
        height: '32px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: isActive ? '0.35' : '1',
        color: '#6B7280',
      });
    });
  }, [displayData]);

  const getEventColor = (isPromotion: boolean, discount: string) => {
    if (isPromotion) return '#8b5cf6'; // violet-500
    if (parseFloat(discount) > 0) return '#22c55e'; // green-500
    // blue for custom price
    return '#2563EB'; // blue-500
  };

  const getEventTitle = (price: string, discount: string, isPromotion: boolean) => {
    const formattedPrice = `${price}€`;
    if (isPromotion) return `${formattedPrice} 🎉`;
    if (parseFloat(discount) > 0) return `${formattedPrice} 🏷️`;
    return formattedPrice;
  };

  const events = React.useMemo(() => {
    if (!displayData?.prices) return [];

    // Get the start and end of the current month
    const start = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const end = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

    // Create a Set of dates that have custom prices
    const customPriceDates = new Set(
      displayData.prices.map(price => price.active_at)
    );

    // Create base price events for days without custom prices
    const basePriceEvents = [];
    let currentDay = new Date(start);

    // Get all custom prices by date for quick lookup
    const customPricesByDate = new Map(
      displayData.prices.map(price => [price.active_at, price])
    );

    while (currentDay <= end) {
      const dateStr = currentDay.toLocaleDateString('en-CA');
      const customPrice = customPricesByDate.get(dateStr);

      // Show base price only if there's no custom price for this day
      if (!customPrice) {
        basePriceEvents.push({
          title: `${defaultPrice}€`,
          start: dateStr,
          allDay: true,
          display: 'block',
          backgroundColor: 'transparent',
          borderColor: '#E5E7EB',
          textColor: '#6B7280',
          classNames: ['base-price-event']
        });
      }
      currentDay = new Date(currentDay.setDate(currentDay.getDate() + 1));
    }

    // Create custom price events
    const customPriceEvents = displayData.prices.map(price => {
      const backgroundColor = getEventColor(price.is_promotion, price.discount);
      const title = getEventTitle(price.price, price.discount, price.is_promotion);

      return {
        title,
        start: price.active_at,
        allDay: true,
        backgroundColor,
        borderColor: backgroundColor,
        textColor: '#fff',
        extendedProps: {
          price_default: price.price_default,
          discount: price.discount,
          is_promotion: price.is_promotion,
          description: price.description
        }
      };
    });

    const allEvents = [...basePriceEvents, ...customPriceEvents];
    return allEvents;
  }, [displayData, currentDate, defaultPrice]);

  return (
    <>
      <div className="rounded-2xl border border-stroke bg-white p-4 dark:border-gray-600 dark:bg-gray-800 md:p-6 xl:p-7.5">
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 md:gap-0">
          <div className="flex items-center w-full md:w-auto justify-between md:justify-start gap-4">
            <h2 className="text-xl font-semibold dark:text-white">{t('cars.price_calendar.title')}</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={openPriceRangeEditModal}
            >
              {t('cars.price_calendar.add_range_price')}
            </Button>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#2563EB]"></span>
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('cars.price_calendar.custom_price')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#22c55e]"></span>
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('cars.price_calendar.discount')}</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-[#8b5cf6]"></span>
              <span className="text-sm text-gray-600 dark:text-gray-400">{t('cars.price_calendar.promotion')}</span>
            </div>
          </div>
        </div>
        <div className={`calendar-container ${isLoading ? 'loading' : ''}`}>
          <style>
            {`
              .calendar-container {
                min-height: 800px;
                position: relative;
              }
              .calendar-container.loading::after {
                content: '';
                position: absolute;
                inset: 0;
                background: rgba(255, 255, 255, 0.7);
                z-index: 10;
              }
              .calendar-container.loading::before {
                content: 'Loading...';
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                z-index: 11;
                font-size: 1.2rem;
                color: #4b5563;
              }
              .fc-dayGridMonth-view {
                border: 1px solid #E5E7EB;
                border-top: none !important;
              }
              .fc-daygrid-day-frame {
                min-height: 100px !important;
                cursor: pointer !important;
              }
              .fc-daygrid-day-top {
                flex-direction: row !important;
                justify-content: space-between !important;
                padding: 4px !important;
              }
              .base-price-event {
                cursor: pointer !important;
                font-size: 0.875rem !important;
                color: #6B7280 !important;
                transition: all 0.2s !important;
                background-color: transparent !important;
                margin: 2px 4px !important;
                border-radius: 4px !important;
              }
              .base-price-event:hover {
                color: #2563EB !important;
                background-color: #F3F4F6 !important;
              }
              .fc-day-today .base-price-event {
                color: #2563EB !important;
                border-color: #2563EB !important;
              }
              .fc-daygrid-event {
                white-space: nowrap !important;
                font-size: 0.875rem !important;
                border-radius: 4px !important;
                padding: 2px 8px !important;
                margin: 2px 4px !important;
                cursor: pointer !important;
                width: fit-content !important;
              }
              .fc-header-toolbar {
                padding-left: 0px !important;
                padding-right: 0px !important;
                align-items: start !important;
              }

              .price-eye-btn:hover {
                opacity: 0.7 !important;
                color: #2563EB !important;
              }
              .fc-day-today .price-eye-btn {
                color: #2563EB !important;
              }
              @media (max-width: 768px) {
                .fc-header-toolbar {
                  margin-bottom: 0px !important;
                }
              }
            `}
          </style>
          <FullCalendar
            plugins={[dayGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: 'prev,next today',
              center: 'title',
              right: ''
            }}
            events={events}
            eventClick={(info) => {
              handleDateClick({ date: info.event.start || new Date(), jsEvent: info.jsEvent });
            }}
            dateClick={handleDateClick}
            datesSet={handleDatesSet}
            height="auto"
            firstDay={1}
            fixedWeekCount={true}
            loading={(isLoading) => {
              const calendarEl = document.querySelector('.calendar-container');
              if (calendarEl) {
                if (isLoading) {
                  calendarEl.classList.add('loading');
                } else {
                  calendarEl.classList.remove('loading');
                }
              }
            }}
            locale={i18n.language === 'fr' ? frLocale : undefined}
          />
        </div>
      </div>

      <CarPriceEditModal
        isOpen={!!selectedDate}
        onClose={() => {
          setSelectedDate(null);
          setSelectedPrice(null);
        }}
        onSubmit={handleEditPrice}
        carId={carId}
        selectedDate={selectedDate || new Date()}
        defaultPrice={defaultPrice}
        existingPrice={selectedPrice}
        isDateRangeMode={false}
      />

      <CarPriceEditModal
        isOpen={isPriceRangeEditOpen}
        onClose={closePriceRangeEditModal}
        onSubmit={handleEditPrice}
        carId={carId}
        defaultPrice={defaultPrice}
        existingPrice={null}
        isDateRangeMode={true}
      />
    </>
  );
};
